// ============================================================================
// PATIENT DETAIL PAGE — Ficha 360° del paciente
// Centro neurálgico de gestión del paciente con múltiples bloques
// ============================================================================
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { patientsApi } from '../../api/patients';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { ReminderModal } from '../../components/ui/ReminderModal';
import { toast } from '../../components/ui/Toast';
import { useAuth } from '../../contexts/AuthContext';
import type { Patient, PatientSummary } from '../../types/patient';

import { Patient360Header } from '../../components/patients/Patient360Header';
import { PatientAlerts } from '../../components/patients/PatientAlerts';
import { PatientSitesCard } from '../../components/patients/PatientSitesCard';
import { PatientBudgetsBlock } from '../../components/patients/PatientBudgetsBlock';
import { PatientFinancialBlock } from '../../components/patients/PatientFinancialBlock';
import { PatientRemindersBlock } from '../../components/patients/PatientRemindersBlock';
import { PatientContactsBlock } from '../../components/patients/PatientContactsBlock';
import { PatientDocumentsBlock } from '../../components/patients/PatientDocumentsBlock';
import { PatientNotesBlock } from '../../components/patients/PatientNotesBlock';
import { PatientTimelineBlock } from '../../components/patients/PatientTimelineBlock';

const NAVIGATION_STORAGE_KEY = 'imi_patients_list_ids';

export function PatientDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user, activeSite } = useAuth();

  const [patient, setPatient] = useState<Patient | null>(null);
  const [summary, setSummary] = useState<PatientSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showToggleModal, setShowToggleModal] = useState(false);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [isToggling, setIsToggling] = useState(false);
  const [timelineSignal, setTimelineSignal] = useState(0);

  const userRole = (user?.role || 'USER') as 'USER' | 'GESTOR_SEDE' | 'ADMIN';
  const userId = user?.id;
  const userSiteIds = useMemo<string[]>(() => {
    const raw = (user as any)?.siteIds;
    if (Array.isArray(raw)) return raw;
    return [];
  }, [user]);
  const currentSiteId = activeSite?.id || '';

  // ── Cargar paciente y summary en paralelo ────────────────────────────────
  const load = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    setError(null);
    try {
      const [p, s] = await Promise.all([
        patientsApi.getById(id),
        patientsApi.getSummary(id).catch(() => null as PatientSummary | null),
      ]);
      setPatient(p);
      setSummary(s);
    } catch (err: any) {
      setError(err?.message || 'Error al cargar paciente');
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // ── Refrescar solo el summary (sin bloquear la UI) ───────────────────────
  const bumpTimeline = useCallback(() => setTimelineSignal((n) => n + 1), []);

  const refreshSummary = useCallback(async () => {
    if (!id) return;
    try {
      const s = await patientsApi.getSummary(id);
      setSummary(s);
    } catch {
      /* no-op: no bloqueamos si falla el summary */
    }
  }, [id]);

  // ── Navegación ◀ ▶ entre pacientes del listado ───────────────────────────
  const { prevId, nextId } = useMemo(() => {
    if (!id) return { prevId: null, nextId: null };
    try {
      const raw = sessionStorage.getItem(NAVIGATION_STORAGE_KEY);
      if (!raw) return { prevId: null, nextId: null };
      const ids: string[] = JSON.parse(raw);
      const idx = ids.indexOf(id);
      if (idx < 0) return { prevId: null, nextId: null };
      return {
        prevId: idx > 0 ? ids[idx - 1] : null,
        nextId: idx < ids.length - 1 ? ids[idx + 1] : null,
      };
    } catch {
      return { prevId: null, nextId: null };
    }
  }, [id]);

  // ── Helpers ──────────────────────────────────────────────────────────────
  const isActiveInSite = patient
    ? (patient.isActiveInCurrentSite ?? patient.isActive)
    : false;

  const handleToggleCurrentSite = async () => {
    if (!patient || !id) return;
    setIsToggling(true);
    try {
      if (isActiveInSite) {
        await patientsApi.delete(id);
        toast.success('Paciente dado de baja en este centro');
      } else {
        await patientsApi.reactivate(id);
        toast.success('Paciente reactivado en este centro');
      }
      await load();
    } catch (err: any) {
      toast.error('Error', err?.message || 'No se pudo cambiar el estado');
    } finally {
      setIsToggling(false);
      setShowToggleModal(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/pacientes')} className="p-2 rounded-lg hover:bg-gray-100">
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          <div className="animate-pulse"><div className="h-8 w-48 bg-gray-200 rounded" /></div>
        </div>
        <div className="animate-pulse space-y-4">
          <div className="h-32 bg-gray-100 rounded-xl" />
          <div className="h-48 bg-gray-100 rounded-xl" />
          <div className="h-48 bg-gray-100 rounded-xl" />
        </div>
      </div>
    );
  }

  if (error || !patient) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/pacientes')} className="p-2 rounded-lg hover:bg-gray-100">
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Error</h1>
        </div>
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error || 'Paciente no encontrado'}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Cabecera 360° */}
      <Patient360Header
        patient={patient}
        summary={summary}
        isActiveInSite={isActiveInSite}
        canCreateBudget={isActiveInSite}
        prevPatientId={prevId}
        nextPatientId={nextId}
        onEdit={() => navigate(`/pacientes/${id}/editar`)}
        onNewBudget={() => navigate(`/presupuestos/nuevo?paciente=${id}`)}
        onNewReminder={() => setShowReminderModal(true)}
        onToggleSiteStatus={() => setShowToggleModal(true)}
      />

      {/* Alertas inteligentes */}
      <PatientAlerts summary={summary} patientIsActive={patient.isActive} />

      {/* Bloque 1: Centros */}
      <PatientSitesCard
        patientId={patient.id}
        patientName={patient.fullName}
        linkedSites={patient.linkedSites || []}
        currentSiteId={currentSiteId}
        userRole={userRole}
        userSiteIds={userSiteIds}
        userId={userId}
        onChanged={() => { load(); refreshSummary(); }}
      />
      {/* Bloque: Notas internas (v2) */}
      <PatientNotesBlock patientId={patient.id} userId={userId} onChanged={bumpTimeline} />


      {/* Bloque 2: Presupuestos */}
      <PatientBudgetsBlock patientId={patient.id} userId={userId} />

      {/* Bloque 3: Resumen financiero */}
      <PatientFinancialBlock patientId={patient.id} userId={userId} />

      {/* Bloque 4: Recordatorios */}
      <PatientRemindersBlock
        patientId={patient.id}
        patientName={patient.fullName}
        userId={userId}
        onChanged={refreshSummary}
      />

      {/* Bloque 5: Historial de contactos */}
      <PatientContactsBlock patientId={patient.id} userId={userId} />

      {/* Bloque 6: Documentos adjuntos */}
      <PatientDocumentsBlock patientId={patient.id} userId={userId} />

      {/* Bloque: Timeline de actividad (v2) */}
      <PatientTimelineBlock
        patientId={patient.id}
        userId={userId}
        userRole={userRole}
        refreshSignal={timelineSignal}
      />

      {/* Modales */}
      <ReminderModal
        isOpen={showReminderModal}
        onClose={() => setShowReminderModal(false)}
        onSaved={refreshSummary}
        patientId={id || ''}
        patientName={patient.fullName}
      />

      <ConfirmDialog
        isOpen={showToggleModal}
        onClose={() => !isToggling && setShowToggleModal(false)}
        onConfirm={handleToggleCurrentSite}
        isLoading={isToggling}
        variant={isActiveInSite ? 'warning' : 'info'}
        title={isActiveInSite ? '¿Dar de baja en este centro?' : '¿Reactivar en este centro?'}
        message={
          isActiveInSite
            ? `"${patient.fullName}" será dado de baja en este centro. Sus datos seguirán disponibles en otros centros donde esté vinculado.`
            : `"${patient.fullName}" será reactivado en este centro.`
        }
        confirmText={isActiveInSite ? 'Dar de baja' : 'Reactivar'}
      />
    </div>
  );
}

export default PatientDetailPage;
